import React from 'react';
import { createPortal } from 'react-dom';

import ClickLayer from '../components/base/ClickLayer/ClickLayer.js';
import { IComponentBaseProps } from '../models/IComponentBaseProps.js';

export type TPortalToggleReason = 'clickLayer' | 'buttonClick' | 'itemClick' | 'acceptKey' | 'navigationKey' | 'escapeKey';
type TOnCloseFn =  (e: React.MouseEvent<HTMLDivElement>, reason: TPortalToggleReason) => void;

export interface IPortalProps extends IComponentBaseProps {
	/**
	 * If defined, will render the menu within a portal, otherwise will render in place.
	 * @title Portal Element
	 */
	portalContainer?: React.RefObject<HTMLElement>;
	/**
	 * A flag that allows you to omit the use of the portal.
	 * @title Disable Portal
	 * @default false
	 */
	disablePortal?: boolean;
	/**
	 * The flag which is required by the implementation to show the menu as open/closed
	 * @title Open
	 * @default false
	 */
	open?: boolean;
	/**
	 * The ClickLayer component uses this to close the menu.
	 * @title On Close
	 */
	onClose?: TOnCloseFn;
}

export const stripPortalProps = <P extends IPortalProps>(props: Partial<P>) => {
	const { portalContainer, disablePortal, open, onClose, ...otherProps } = props;
	return otherProps;
}

export const withPortal = <P extends IPortalProps>(WrappedComponent: React.ComponentType<P>) => {
	return (props: P) => {
		const { children, open, onClose, portalContainer, disablePortal = false } = props;
		if (!children) {
			return null;
		}
		if (!open) {
			return null;
		}
		const mountNode = portalContainer?.current || document.body;
		return (
			<>
				<ClickLayer onClick={(e: React.MouseEvent<HTMLDivElement>) => onClose && onClose(e, 'clickLayer')}/>
				{ !disablePortal && createPortal(<WrappedComponent {...props}/>, mountNode) }
				{ disablePortal && <WrappedComponent {...props}/> }
			</>
		)
	}
}